import { useState, useRef } from "react";
import axios from "axios";

const API_BASE = window.location.hostname === "localhost"
  ? "http://localhost:8000"
  : "https://nicolasljulia-openfarm.hf.space";

/** Self-contained Disease Detector modal. Pass `crop` (string) and `onClose`. */
export default function DiseaseModal({ crop, onClose }) {
  const [img,     setImg]     = useState(null);
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const inputRef = useRef(null);

  async function analyze() {
    if (!img) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", img);
      fd.append("crop_type", crop || "unknown");
      const res = await axios.post(`${API_BASE}/api/analyze`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
    } catch {
      setError("Detection failed — check that the backend is running.");
    }
    setLoading(false);
  }

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:2000,
      background:"rgba(0,0,0,0.6)",
      display:"flex", alignItems:"center", justifyContent:"center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:"#fff", borderRadius:14, padding:"22px",
        width:420, maxWidth:"92vw", maxHeight:"88vh",
        overflowY:"auto", boxShadow:"0 8px 40px rgba(0,0,0,0.3)",
      }}>
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:"#1a2e1a" }}>Disease Detector</div>
            <div style={{ fontSize:11, color:"#888", marginTop:2 }}>EfficientNetB3 · PlantVillage · 38 disease classes</div>
          </div>
          <button onClick={onClose}
            style={{ background:"#f0f0e8", border:"none", borderRadius:"50%", width:30, height:30, cursor:"pointer", fontSize:16, color:"#666" }}>✕</button>
        </div>

        {/* Upload area */}
        <div onClick={() => inputRef.current?.click()} style={{
          border:"1.5px dashed #c8ddb8", borderRadius:10,
          padding:"20px 16px", textAlign:"center", cursor:"pointer",
          background: img ? "#f0f9e8" : "#fafaf8", marginBottom:12,
        }}>
          {img ? (
            <>
              <img src={URL.createObjectURL(img)} alt="leaf"
                style={{ maxHeight:160, maxWidth:"100%", borderRadius:8, marginBottom:8 }}/>
              <div style={{ fontSize:11, color:"#2d5a1b", fontWeight:600 }}>{img.name}</div>
              <div style={{ fontSize:10, color:"#aaa", marginTop:2 }}>Click to change</div>
            </>
          ) : (
            <>
              <div style={{ fontSize:32, marginBottom:6 }}>📷</div>
              <div style={{ fontSize:13, fontWeight:600, color:"#444" }}>Upload a leaf photo</div>
              <div style={{ fontSize:11, color:"#aaa", marginTop:4 }}>JPG, PNG, WEBP · any size</div>
            </>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" style={{ display:"none" }}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) { setImg(f); setResult(null); }
          }}/>

        {img && (
          <button onClick={analyze} disabled={loading} style={{
            width:"100%", padding:"12px", fontSize:14, fontWeight:600,
            background: loading ? "#7aab5a" : "#2d5a1b",
            color:"#fff", border:"none", borderRadius:8,
            cursor: loading ? "default" : "pointer", marginBottom:12,
          }}>
            {loading ? "Analyzing…" : "Detect Disease"}
          </button>
        )}

        {error && (
          <div style={{ background:"#fef2f2", border:"1px solid #fecaca", color:"#991b1b", borderRadius:7, padding:"10px 12px", fontSize:13, marginBottom:12 }}>
            {error}
          </div>
        )}

        {result && (
          <div style={{
            padding:"14px", borderRadius:10,
            background: result.needs_review ? "#FFF8EE" : "#f0f9e8",
            border:`1px solid ${result.needs_review ? "#F5D9A0" : "#C2DFA8"}`,
          }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
              <div>
                <div style={{ fontSize:17, fontWeight:700, color:"#1a2e1a" }}>{result.disease}</div>
                <div style={{ fontSize:12, color:"#888", marginTop:2 }}>{result.crop}</div>
              </div>
              <div style={{
                fontSize:15, fontWeight:700, padding:"4px 12px", borderRadius:20,
                color:  result.confidence>0.8?"#639922":result.confidence>0.5?"#EF9F27":"#E24B4A",
                background: result.confidence>0.8?"#EAF3DE":result.confidence>0.5?"#FFF8EE":"#FCEBEB",
              }}>
                {(result.confidence*100).toFixed(0)}%
              </div>
            </div>
            <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
              <span style={{
                fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:20,
                color:  {High:"#E24B4A",Medium:"#EF9F27",Low:"#639922"}[result.severity],
                background: {High:"#FCEBEB",Medium:"#FFF8EE",Low:"#EAF3DE"}[result.severity],
              }}>{result.severity} confidence</span>
              {result.needs_review && (
                <span style={{ fontSize:10, fontWeight:600, color:"#B85C00", background:"#FFF0DC", padding:"2px 8px", borderRadius:20 }}>
                  ⚠ Expert review suggested
                </span>
              )}
            </div>
            <div style={{ fontSize:10, color:"#999", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Top 3 predictions</div>
            {result.top3?.map((item, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <div style={{ fontSize:12, color:"#444", flex:1 }}>
                  {item.disease} <span style={{ color:"#bbb" }}>· {item.crop}</span>
                </div>
                <div style={{ width:80, height:5, background:"#eee", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", background:i===0?"#2d5a1b":"#a8c890", width:`${item.confidence*100}%`, borderRadius:3 }}/>
                </div>
                <div style={{ fontSize:11, fontWeight:600, color:"#888", width:32, textAlign:"right" }}>
                  {(item.confidence*100).toFixed(0)}%
                </div>
              </div>
            ))}
            <button
              onClick={() => { setImg(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; }}
              style={{
                marginTop:12, width:"100%", padding:"9px",
                fontSize:12, fontWeight:600,
                background:"#f5f5f0", color:"#2d5a1b",
                border:"1px solid #c8ddb8", borderRadius:8, cursor:"pointer",
              }}>
              Try another image
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
